<template>
  <AppLayout>
    <div class="flex justify-between items-center mb-4 mt-4">
      <h1>External Product Images</h1>
    </div>

    <div class="mb-4 flex gap-4 items-center">
      <IconField>
        <InputIcon class="pi pi-search" />
        <InputText
          v-model="searchQuery"
          placeholder="Search by product name or filename..."
          class="w-80"
        />
      </IconField>
      <Select
        v-model="selectedFilter"
        :options="filterOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Filter"
        class="w-56"
        @change="onFilterChange"
      />
      <Select
        v-model="selectedPriority"
        :options="priorityOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Priority"
        class="w-40"
        @change="onFilterChange"
      />
      <Select
        v-model="imageSize"
        :options="imageSizeOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Image Size"
        class="w-36"
      />
    </div>

    <DataTable
      :value="images"
      :loading="loading"
      paginator
      :rows="50"
      :rowsPerPageOptions="[50, 100, 200]"
      :totalRecords="totalRecords"
      :lazy="true"
      @page="onPage"
      tableStyle="min-width: 50rem"
      class="hoverable-rows"
    >
      <!-- Image previews column -->
      <Column header="Images" :style="{ width: imageColumnWidth }">
        <template #body="{ data }">
          <div class="flex gap-2 cursor-pointer" @click="openLightbox(data)">
            <!-- Original image -->
            <div class="flex flex-col items-center">
              <img
                v-if="data.originalImageUrl || data.url"
                :src="data.originalImageUrl || data.url"
                :alt="data.fileName"
                :class="imageSizeClass"
                class="object-cover rounded border border-surface-200"
                loading="lazy"
              />
              <div
                v-else
                :class="imageSizeClass"
                class="bg-surface-100 rounded flex items-center justify-center border border-surface-200"
              >
                <i class="pi pi-image text-surface-400"></i>
              </div>
              <span class="text-xs text-surface-500 mt-1">Original</span>
            </div>
            <!-- Selected/Processed image -->
            <div class="flex flex-col items-center">
              <img
                v-if="data.selectedImageUrl"
                :src="data.selectedImageUrl"
                :alt="`${data.fileName} (processed)`"
                :class="imageSizeClass"
                class="object-cover rounded border border-surface-200"
                loading="lazy"
              />
              <div
                v-else
                :class="imageSizeClass"
                class="bg-surface-100 rounded flex items-center justify-center border border-dashed border-surface-300"
              >
                <i class="pi pi-sparkles text-surface-400"></i>
              </div>
              <span class="text-xs text-surface-500 mt-1">Selected</span>
            </div>
          </div>
        </template>
      </Column>

      <!-- Product Name -->
      <Column field="productName" header="Product">
        <template #body="{ data }">
          <div class="flex flex-col">
            <span class="font-medium">{{ data.productName || '—' }}</span>
            <small class="text-surface-500">{{ data.fileName }}</small>
          </div>
        </template>
      </Column>

      <!-- External Product Image Status -->
      <Column header="Image Status" style="width: 280px">
        <template #body="{ data }">
          <div class="flex flex-col gap-1">
            <!-- Downloaded status -->
            <div class="flex items-center gap-2">
              <i
                class="pi text-sm"
                :class="
                  data.status?.downloadedAt
                    ? 'pi-check-circle text-green-500'
                    : 'pi-circle text-surface-300'
                "
              ></i>
              <span
                class="text-sm"
                :class="data.status?.downloadedAt ? 'text-green-700' : 'text-surface-500'"
              >
                Downloaded
              </span>
              <span v-if="data.status?.downloadedAt" class="text-xs text-surface-400">
                {{ formatDate(data.status.downloadedAt) }}
              </span>
            </div>
            <!-- Processed status -->
            <div class="flex items-center gap-2">
              <i
                class="pi text-sm"
                :class="
                  data.status?.processedAt
                    ? 'pi-check-circle text-green-500'
                    : 'pi-circle text-surface-300'
                "
              ></i>
              <span
                class="text-sm"
                :class="data.status?.processedAt ? 'text-green-700' : 'text-surface-500'"
              >
                Processed
              </span>
              <span v-if="data.status?.processedAt" class="text-xs text-surface-400">
                {{ formatDate(data.status.processedAt) }}
              </span>
            </div>
            <!-- Uploaded status -->
            <div class="flex items-center gap-2">
              <i
                class="pi text-sm"
                :class="
                  data.status?.uploadedAt
                    ? 'pi-check-circle text-green-500'
                    : 'pi-circle text-surface-300'
                "
              ></i>
              <span
                class="text-sm"
                :class="data.status?.uploadedAt ? 'text-green-700' : 'text-surface-500'"
              >
                Uploaded
              </span>
              <span v-if="data.status?.uploadedAt" class="text-xs text-surface-400">
                {{ formatDate(data.status.uploadedAt) }}
              </span>
            </div>
            <!-- Current action -->
            <div v-if="data.status?.action" class="flex items-center gap-2 mt-1">
              <ProgressSpinner style="width: 14px; height: 14px" strokeWidth="4" />
              <span class="text-sm text-blue-600">{{ formatAction(data.status.action) }}</span>
            </div>
            <!-- Error -->
            <div v-if="data.status?.error" class="flex items-center gap-2 mt-1">
              <i class="pi pi-exclamation-triangle text-red-500 text-sm"></i>
              <span class="text-sm text-red-600 truncate" :title="data.status.error">
                {{ data.status.error.slice(0, 30) }}{{ data.status.error.length > 30 ? '...' : '' }}
              </span>
            </div>
          </div>
        </template>
      </Column>

      <!-- Image Edit Status -->
      <Column header="Edit Pipeline" style="width: 180px">
        <template #body="{ data }">
          <div v-if="data.imageEditStatus" class="flex flex-col gap-1">
            <!-- Progress -->
            <div class="flex items-center gap-2">
              <span class="text-sm text-surface-600">
                {{ (data.imageEditStatus.finishedOpIdx ?? -1) + 1 }} /
                {{ data.imageEditStatus.totalOperations ?? 0 }} ops
              </span>
            </div>
            <!-- Progress bar -->
            <div class="w-full bg-surface-200 rounded-full h-2">
              <div
                class="h-2 rounded-full transition-all duration-300"
                :class="data.imageEditStatus.error ? 'bg-red-500' : 'bg-green-500'"
                :style="{
                  width: `${getProgressPercent(data.imageEditStatus)}%`,
                }"
              ></div>
            </div>
            <!-- Current operation -->
            <div
              v-if="
                data.imageEditStatus.startedOpIdx !== undefined &&
                data.imageEditStatus.startedOpIdx !== null
              "
              class="flex items-center gap-1"
            >
              <ProgressSpinner style="width: 12px; height: 12px" strokeWidth="4" />
              <span class="text-xs text-blue-600"
                >Running op #{{ data.imageEditStatus.startedOpIdx + 1 }}</span
              >
            </div>
            <!-- Error -->
            <div v-if="data.imageEditStatus.error" class="flex items-center gap-1 mt-1">
              <i class="pi pi-exclamation-triangle text-red-500 text-xs"></i>
              <span class="text-xs text-red-600 truncate" :title="data.imageEditStatus.error">
                {{ data.imageEditStatus.error.slice(0, 25) }}...
              </span>
            </div>
          </div>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>

      <!-- Metadata -->
      <Column header="Dimensions" style="width: 120px">
        <template #body="{ data }">
          <div v-if="data.metadata?.width && data.metadata?.height" class="text-sm">
            {{ data.metadata.width }} × {{ data.metadata.height }}
          </div>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>

      <!-- Created -->
      <Column field="created" header="Created" style="width: 140px">
        <template #body="{ data }">
          {{ formatDate(data.created) }}
        </template>
      </Column>
    </DataTable>

    <!-- Lightbox Dialog -->
    <Dialog
      v-model:visible="lightboxVisible"
      modal
      :style="{ width: '90vw', maxWidth: '1400px' }"
      :contentStyle="{ padding: '1.5rem' }"
      @hide="onLightboxClose"
    >
      <template #header>
        <div class="flex items-center justify-between w-full pr-8">
          <div class="flex items-center gap-4">
            <span class="text-xl font-semibold">{{
              lightboxImage?.productName || 'Image Comparison'
            }}</span>
            <span class="text-sm text-surface-500">
              {{ currentImageIndex + 1 }} / {{ images.length }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <Button
              icon="pi pi-chevron-left"
              severity="secondary"
              size="small"
              :disabled="!hasPrevImage"
              @click="goToPrevImage"
              v-tooltip.bottom="'Previous (←)'"
            />
            <Button
              icon="pi pi-chevron-right"
              severity="secondary"
              size="small"
              :disabled="!hasNextImage"
              @click="goToNextImage"
              v-tooltip.bottom="'Next (→)'"
            />
            <Button
              icon="pi pi-link"
              label="Copy link"
              severity="secondary"
              size="small"
              @click="copyLightboxLink"
              v-tooltip.bottom="'Copy shareable link'"
            />
          </div>
        </div>
      </template>
      <div v-if="lightboxImage" class="flex flex-col gap-4">
        <!-- Image comparison -->
        <div class="flex gap-6 justify-center">
          <!-- Original -->
          <div class="flex flex-col items-center flex-1 max-w-[45%]">
            <div class="text-sm font-medium text-surface-600 mb-2">Original</div>
            <div class="relative group">
              <img
                v-if="lightboxImage.originalImageUrl || lightboxImage.url"
                :src="lightboxImage.originalImageUrl || lightboxImage.url"
                :alt="lightboxImage.fileName"
                class="max-h-[60vh] max-w-full object-contain rounded-lg border border-surface-200"
              />
              <div
                v-else
                class="w-64 h-64 bg-surface-100 rounded-lg flex items-center justify-center border border-surface-200"
              >
                <i class="pi pi-image text-surface-400 text-4xl"></i>
              </div>
              <!-- Open in new tab button -->
              <button
                v-if="lightboxImage.originalImageUrl || lightboxImage.url"
                @click="openInNewTab(lightboxImage.originalImageUrl || lightboxImage.url)"
                class="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <i class="pi pi-external-link text-xs"></i>
                Open full res
              </button>
            </div>
            <div class="text-xs text-surface-500 mt-2">{{ lightboxImage.fileName }}</div>
          </div>

          <!-- Selected/Processed -->
          <div class="flex flex-col items-center flex-1 max-w-[45%]">
            <div class="text-sm font-medium text-surface-600 mb-2">Selected (Processed)</div>
            <div class="relative group">
              <img
                v-if="lightboxImage.selectedImageUrl"
                :src="lightboxImage.selectedImageUrl"
                :alt="`${lightboxImage.fileName} (processed)`"
                class="max-h-[60vh] max-w-full object-contain rounded-lg border border-surface-200"
              />
              <div
                v-else
                class="w-64 h-64 bg-surface-100 rounded-lg flex items-center justify-center border border-dashed border-surface-300"
              >
                <div class="text-center">
                  <i class="pi pi-sparkles text-surface-400 text-4xl"></i>
                  <div class="text-surface-500 text-sm mt-2">Not processed yet</div>
                </div>
              </div>
              <!-- Open in new tab button -->
              <button
                v-if="lightboxImage.selectedImageUrl"
                @click="openInNewTab(lightboxImage.selectedImageUrl)"
                class="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-lg px-3 py-1.5 text-sm flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <i class="pi pi-external-link text-xs"></i>
                Open full res
              </button>
            </div>
            <div v-if="lightboxImage.selectedImageUrl" class="text-xs text-surface-500 mt-2">
              Processed version
            </div>
          </div>
        </div>

        <!-- Image info -->
        <div class="flex justify-center gap-8 pt-4 border-t border-surface-200">
          <div
            v-if="lightboxImage.metadata?.width && lightboxImage.metadata?.height"
            class="text-sm"
          >
            <span class="text-surface-500">Original dimensions:</span>
            <span class="ml-1 font-medium"
              >{{ lightboxImage.metadata.width }} × {{ lightboxImage.metadata.height }}</span
            >
          </div>
          <div v-if="lightboxImage.status?.processedAt" class="text-sm">
            <span class="text-surface-500">Processed:</span>
            <span class="ml-1 font-medium">{{
              formatDateFull(lightboxImage.status.processedAt)
            }}</span>
          </div>
        </div>

        <!-- Expandable intermediate steps -->
        <div
          v-if="lightboxImage.versions && lightboxImage.versions.length > 0"
          class="border-t border-surface-200 pt-4"
        >
          <button
            @click="stepsExpanded = !stepsExpanded"
            class="flex items-center gap-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors cursor-pointer bg-transparent border-none p-0"
          >
            <i
              class="pi text-xs transition-transform"
              :class="stepsExpanded ? 'pi-chevron-down' : 'pi-chevron-right'"
            ></i>
            Processing Steps ({{ lightboxImage.versions.length }} versions)
          </button>

          <div v-if="stepsExpanded" class="mt-4">
            <div class="flex gap-4 overflow-x-auto pb-4 versions-scroll">
              <div
                v-for="(version, index) in lightboxImage.versions"
                :key="version.id"
                class="flex-shrink-0 flex flex-col items-center"
              >
                <div class="relative group">
                  <img
                    v-if="version.url"
                    :src="version.url"
                    :alt="`Step ${index + 1}`"
                    class="w-32 h-32 object-contain rounded-lg border bg-surface-50"
                    :class="{
                      'ring-4 ring-blue-500 border-blue-500':
                        version.id === lightboxImage.selectedVersionId,
                      'border-green-400 border-2':
                        version.original && version.id !== lightboxImage.selectedVersionId,
                      'border-purple-400 border-2':
                        !version.original &&
                        !version.intermediate &&
                        version.id !== lightboxImage.selectedVersionId,
                      'border-surface-200':
                        version.intermediate && version.id !== lightboxImage.selectedVersionId,
                    }"
                  />
                  <div
                    v-else
                    class="w-32 h-32 bg-surface-100 rounded-lg flex items-center justify-center border border-surface-200"
                  >
                    <i class="pi pi-image text-surface-400"></i>
                  </div>
                  <!-- Open in new tab button -->
                  <button
                    v-if="version.url"
                    @click="openInNewTab(version.url)"
                    class="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded px-2 py-1 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i class="pi pi-external-link text-xs"></i>
                  </button>
                </div>
                <div class="mt-2 text-center">
                  <div
                    v-if="version.id === lightboxImage.selectedVersionId"
                    class="text-xs font-bold text-blue-600"
                  >
                    ✓ Selected
                  </div>
                  <div class="text-xs font-medium text-surface-700">
                    {{ getVersionLabel(version, index) }}
                  </div>
                  <div class="text-xs text-surface-500">
                    {{ version.key || `Step ${index + 1}` }}
                  </div>
                  <div class="text-xs text-surface-400">
                    {{ formatDate(version.created) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import dayjs from 'dayjs';
import AppLayout from '@/components/AppLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Select from 'primevue/select';
import ProgressSpinner from 'primevue/progressspinner';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import * as Sentry from '@sentry/vue';

interface ExternalProductImageStatus {
  action?: string;
  downloadedAt?: number;
  processedAt?: number;
  uploadedAt?: number;
  error?: string;
}

interface ImageEditStatus {
  finishedOpIdx?: number;
  startedOpIdx?: number;
  startedOpDateTime?: number;
  error?: string;
  totalOperations?: number;
}

interface ExternalProductImageMetadata {
  width?: number;
  height?: number;
}

interface ImageVersion {
  id: string;
  imageId: string;
  original: boolean;
  intermediate: boolean;
  key?: string;
  url?: string;
  created: number;
  modified: number;
}

interface ExternalProductImage {
  id: string;
  orgId: string;
  externalProductId: string;
  externalProductGuid: string;
  externalImageGuid: string;
  url: string;
  fileName: string;
  priority: number;
  productName?: string;
  imageId?: string;
  originalImageUrl?: string;
  selectedImageUrl?: string;
  selectedVersionId?: string;
  status?: ExternalProductImageStatus;
  metadata?: ExternalProductImageMetadata;
  imageEditStatus?: ImageEditStatus;
  versions?: ImageVersion[];
  created: number;
  modified: number;
}

const route = useRoute();
const router = useRouter();
const toast = useToast();

const images = ref<ExternalProductImage[]>([]);
const loading = ref(false);
const totalRecords = ref(0);
const currentOffset = ref(0);
const currentLimit = ref(50);
const searchQuery = ref('');
const selectedFilter = ref<string>('all');
const selectedPriority = ref<number | null>(null);
const imageSize = ref<string>('medium');

// Lightbox state
const lightboxVisible = ref(false);
const lightboxImage = ref<ExternalProductImage | null>(null);
const stepsExpanded = ref(false);

// Navigation computed properties
const currentImageIndex = computed(() => {
  if (!lightboxImage.value) return -1;
  return images.value.findIndex(img => img.id === lightboxImage.value!.id);
});

const hasPrevImage = computed(() => currentImageIndex.value > 0);
const hasNextImage = computed(() => currentImageIndex.value < images.value.length - 1);

const filterOptions = [
  { label: 'All Images', value: 'all' },
  { label: 'Not Downloaded', value: 'notDownloaded' },
  { label: 'Downloaded', value: 'downloaded' },
  { label: 'Not Processed', value: 'notProcessed' },
  { label: 'Processed', value: 'processed' },
  { label: 'Not Uploaded', value: 'notUploaded' },
  { label: 'Uploaded', value: 'uploaded' },
  { label: 'Has Error', value: 'hasError' },
  { label: 'In Progress', value: 'inProgress' },
];

const priorityOptions = [
  { label: 'All Priorities', value: null },
  { label: 'Priority 1', value: 1 },
  { label: 'Priority 2', value: 2 },
  { label: 'Priority 3', value: 3 },
  { label: 'Priority 4', value: 4 },
  { label: 'Priority 5', value: 5 },
];

const imageSizeOptions = [
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
];

const imageSizeClass = computed(() => {
  switch (imageSize.value) {
    case 'small':
      return 'w-12 h-12';
    case 'large':
      return 'w-40 h-40';
    default:
      return 'w-16 h-16';
  }
});

const imageColumnWidth = computed(() => {
  switch (imageSize.value) {
    case 'small':
      return '140px';
    case 'large':
      return '380px';
    default:
      return '180px';
  }
});

async function loadImages() {
  try {
    loading.value = true;
    const params = new URLSearchParams({
      search: searchQuery.value,
      offset: String(currentOffset.value),
      limit: String(currentLimit.value),
      filter: selectedFilter.value,
    });
    if (selectedPriority.value !== null) {
      params.set('priority', String(selectedPriority.value));
    }
    const response = await fetch(`/api/external-products/images?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to load images');
    }
    const data = await response.json();
    images.value = data.data ?? [];
    totalRecords.value = data.total ?? 0;
  } catch (err) {
    console.error('Failed to load external product images', err);
  } finally {
    loading.value = false;
  }
}

function onFilterChange() {
  currentOffset.value = 0;
  loadImages();
}

const debouncedSearch = useDebounceFn(() => {
  currentOffset.value = 0;
  loadImages();
}, 300);

watch(searchQuery, () => {
  debouncedSearch();
});

function onPage(event: { first: number; rows: number }) {
  currentOffset.value = event.first;
  currentLimit.value = event.rows;
  loadImages();
}

function formatDate(timestamp: number | undefined): string {
  if (!timestamp) return '—';
  return dayjs(timestamp).format('MMM D, HH:mm');
}

function formatDateFull(timestamp: number | undefined): string {
  if (!timestamp) return '—';
  return dayjs(timestamp).format('MMM D, YYYY HH:mm:ss');
}

function formatAction(action: string): string {
  const actionMap: Record<string, string> = {
    download: 'Downloading...',
    metadataImageSize: 'Getting metadata...',
    process: 'Processing...',
    upload: 'Uploading...',
  };
  return actionMap[action] || action;
}

function getProgressPercent(status: ImageEditStatus): number {
  if (!status.totalOperations || status.totalOperations === 0) return 0;
  const finished = (status.finishedOpIdx ?? -1) + 1;
  return Math.round((finished / status.totalOperations) * 100);
}

function openLightbox(image: ExternalProductImage) {
  lightboxImage.value = image;
  lightboxVisible.value = true;
  stepsExpanded.value = false;
  // Update URL with image ID
  router.replace({ query: { ...route.query, image: image.id } });
}

function onLightboxClose() {
  // Remove image ID from URL when closing
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { image, ...rest } = route.query;
  router.replace({ query: rest });
}

async function copyLightboxLink() {
  if (!lightboxImage.value) return;
  const url = `${window.location.origin}${window.location.pathname}?image=${lightboxImage.value.id}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.add({
      severity: 'success',
      summary: 'Link copied',
      detail: 'Shareable link copied to clipboard',
      life: 3000,
    });
  } catch (err) {
    Sentry.captureException(err);
    toast.add({
      severity: 'error',
      summary: 'Failed to copy',
      detail: 'Could not copy link to clipboard',
      life: 3000,
    });
  }
}

function openLightboxById(imageId: string) {
  const image = images.value.find(img => img.id === imageId);
  if (image) {
    lightboxImage.value = image;
    lightboxVisible.value = true;
    stepsExpanded.value = false;
  }
}

function goToPrevImage() {
  if (!hasPrevImage.value) return;
  const prevImage = images.value[currentImageIndex.value - 1];
  if (prevImage) {
    lightboxImage.value = prevImage;
    stepsExpanded.value = false;
    router.replace({ query: { ...route.query, image: prevImage.id } });
  }
}

function goToNextImage() {
  if (!hasNextImage.value) return;
  const nextImage = images.value[currentImageIndex.value + 1];
  if (nextImage) {
    lightboxImage.value = nextImage;
    stepsExpanded.value = false;
    router.replace({ query: { ...route.query, image: nextImage.id } });
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!lightboxVisible.value) return;

  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    goToPrevImage();
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    goToNextImage();
  }
}

function getVersionLabel(version: ImageVersion, index: number): string {
  if (version.original) return 'Original';
  if (!version.intermediate) return 'Final';
  return `Step ${index}`;
}

function openInNewTab(url: string) {
  // Append =s0 for full resolution
  const fullResUrl = url.includes('?') ? `${url}&s=0` : `${url}=s0`;
  window.open(fullResUrl, '_blank');
}

// Check for image ID in URL after images are loaded
watch(
  images,
  newImages => {
    const imageId = route.query.image as string | undefined;
    if (imageId && newImages.length > 0 && !lightboxVisible.value) {
      openLightboxById(imageId);
    }
  },
  { immediate: true },
);

// Keyboard navigation
onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
});

// Initial load
loadImages();
</script>

<style scoped>
.hoverable-rows :deep(tbody tr) {
  transition: background-color 0.2s ease;
}

.hoverable-rows :deep(tbody tr:hover) {
  background-color: var(--p-surface-100);
}

.versions-scroll {
  scrollbar-width: thin;
  scrollbar-color: var(--p-surface-300) transparent;
}

.versions-scroll::-webkit-scrollbar {
  height: 8px;
}

.versions-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.versions-scroll::-webkit-scrollbar-thumb {
  background-color: var(--p-surface-300);
  border-radius: 4px;
}

.versions-scroll::-webkit-scrollbar-thumb:hover {
  background-color: var(--p-surface-400);
}
</style>
